// frontend/src/components/features/HouseRulesSection.jsx
import React, { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { authService } from "../../services/auth";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import logger from "../../utils/logger";
import './ModalComponents.css';

export const HOUSE_RULES_CACHE = new Map();

function FileViewer({ attachment, onClose }) {
  const [content, setContent] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const objectUrlRef = useRef(null);

  const viewUrl = attachment.url?.startsWith('http')
    ? attachment.url
    : `${API_BASE}${attachment.url}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const fetchFile = async () => {
      try {
        const isText = attachment.content_type?.startsWith('text/') || attachment.content_type === 'application/json';
        const res = await httpRequest(viewUrl, {
          headers: authService.getAuthHeaders(),
          ...(!isText ? { responseType: 'blob' } : {}),
        });

        if (isText) {
          const text = await res.text();
          if (!cancelled) setContent(text);
          const blob = new Blob([text], { type: attachment.content_type || 'text/plain' });
          const url = URL.createObjectURL(blob);
          objectUrlRef.current = url;
          if (!cancelled) setObjectUrl(url);
        } else {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          objectUrlRef.current = url;
          if (!cancelled) setObjectUrl(url);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load file');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchFile();
    return () => {
      cancelled = true;
      if (objectUrlRef.current && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      objectUrlRef.current = null;
    };
  }, [viewUrl, attachment.content_type]);

  const handleDownload = async () => {
    try {
      if (attachment.signed_url) {
        if (Capacitor.isNativePlatform()) {
          await Browser.open({ url: attachment.signed_url });
        } else {
          window.open(attachment.signed_url, '_blank', 'noopener,noreferrer');
        }
        return;
      }

      // Fallback to the authenticated backend proxy while signed URLs are not
      // yet available from the backend.
      const res = await httpRequest(`${viewUrl}?download=1`, {
        headers: authService.getAuthHeaders(),
        responseType: 'blob',
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      logger.error('Download failed', err);
      setError(err.message || 'Could not download file');
    }
  };

  const renderPreview = () => {
    if (attachment.content_type?.startsWith('image/')) {
      return objectUrl ? (
        <img src={objectUrl} alt={attachment.filename} className="file-viewer-image" />
      ) : null;
    }
    if (attachment.content_type?.startsWith('video/')) {
      return objectUrl ? (
        <video controls className="file-viewer-video" src={objectUrl} />
      ) : null;
    }
    if (attachment.content_type?.startsWith('text/') || attachment.content_type === 'application/json') {
      return (
        <pre className="file-viewer-text">{content}</pre>
      );
    }
    return (
      <div className="file-viewer-fallback">
        Preview not available for this file type.
      </div>
    );
  };

  return (
    <div className="file-viewer-overlay" onClick={onClose}>
      <div className="file-viewer" onClick={(e) => e.stopPropagation()}>
        <div className="file-viewer-header">
          <h3 className="file-viewer-title">{attachment.filename}</h3>
          <div className="file-viewer-actions">
            <button
              className="file-viewer-download"
              onClick={handleDownload}
              aria-label={`Download ${attachment.filename}`}
              data-testid="file-viewer-download"
            >
              Download
            </button>
            <button
              className="file-viewer-close"
              onClick={onClose}
              aria-label="Close file viewer"
              data-testid="file-viewer-close"
            >
              ×
            </button>
          </div>
        </div>
        <div className="file-viewer-body">
          {loading && <span className="house-rules-status">Loading...</span>}
          {error && <div className="house-rules-message house-rules-message--error">{error}</div>}
          {!loading && !error && renderPreview()}
        </div>
      </div>
    </div>
  );
}

function HouseRulesSection({ gameId, user, isAnonymous }) {
  const [notes, setNotes] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const fileInputRef = useRef(null);

  const cacheKey = `${user?.id || 'anon'}-${gameId}`;
  const canEdit = !isAnonymous && !!user?.id;

  useEffect(() => {
    if (isAnonymous || !gameId || !user?.id) {
      setNotes("");
      setAttachments([]);
      return;
    }

    if (HOUSE_RULES_CACHE.has(cacheKey)) {
      const cached = HOUSE_RULES_CACHE.get(cacheKey);
      setNotes(cached.notes);
      setAttachments(cached.attachments);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    httpRequest(`${API_BASE}/house-rules/${gameId}`, {
      method: "GET",
      headers: authService.getAuthHeaders(),
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || "Failed to load house rules");
        }
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setNotes(data.notes || "");
          setAttachments(data.attachments || []);
          HOUSE_RULES_CACHE.set(cacheKey, data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          logger.error("Failed to load house rules:", err);
          setMessage({ text: "Could not load saved info.", type: "error" });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [gameId, user?.id, isAnonymous, cacheKey]);

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleNotesChange = (e) => {
    setNotes(e.target.value);
    const currentCache = HOUSE_RULES_CACHE.get(cacheKey) || { notes: "", attachments };
    HOUSE_RULES_CACHE.set(cacheKey, { ...currentCache, notes: e.target.value });
  };

  const saveNotes = async () => {
    if (!canEdit || !gameId) return;
    setIsSaving(true);
    try {
      const res = await httpRequest(`${API_BASE}/house-rules/${gameId}/notes`, {
        method: "PUT",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ notes }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Failed to save notes");
      }
      const currentCache = HOUSE_RULES_CACHE.get(cacheKey) || { notes, attachments };
      HOUSE_RULES_CACHE.set(cacheKey, { ...currentCache, notes });
      showMessage("Notes saved.", "success");
    } catch (err) {
      logger.error("Failed to save notes:", err);
      showMessage(err.message || "Could not save notes.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const totalSize = attachments.reduce((sum, a) => sum + (a.file_size || 0), 0);
  const totalMB = totalSize / (1024 * 1024);

  const uploadOneFile = async (file) => {
    const formData = new FormData();
    // CapacitorHttp's patched fetch on Android cannot serialise a full File object
    // (it triggers a stack overflow), so pass a plain Blob from file.slice().
    // Preserve the original file type; file.slice() with no type loses it.
    const filePart = Capacitor.isNativePlatform() ? file.slice(0, file.size, file.type || "") : file;
    formData.append("file", filePart, file.name);

    try {
      let res;
      const uploadUrl = `${API_BASE}/house-rules/${gameId}/attachments`;
      const headers = authService.getAuthHeaders();

      if (Capacitor.isNativePlatform()) {
        res = await fetch(uploadUrl, { method: "POST", headers, body: formData });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || "Failed to upload file");
        }
      } else {
        res = await httpRequest(uploadUrl, { method: "POST", headers, body: formData });
      }
      const newAttachment = await res.json();
      setAttachments((prev) => {
        const updated = [newAttachment, ...prev];
        const currentCache = HOUSE_RULES_CACHE.get(cacheKey) || { notes, attachments: prev };
        HOUSE_RULES_CACHE.set(cacheKey, { ...currentCache, attachments: updated });
        return updated;
      });
      showMessage(`${file.name} uploaded.`, "success");
    } catch (err) {
      logger.error("Failed to upload attachment:", err);
      showMessage(err.message || `Could not upload ${file.name}`, "error");
    }
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length || !canEdit || !gameId) return;

    setIsUploading(true);
    for (const file of files) {
      await uploadOneFile(file);
    }
    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const deleteAttachment = async (attachmentId) => {
    if (!canEdit || !gameId) return;
    try {
      const res = await httpRequest(`${API_BASE}/house-rules/${gameId}/attachments/${attachmentId}`, {
        method: "DELETE",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Failed to delete file");
      }
      const updated = attachments.filter((a) => a.id !== attachmentId);
      setAttachments(updated);
      const currentCache = HOUSE_RULES_CACHE.get(cacheKey) || { notes, attachments };
      HOUSE_RULES_CACHE.set(cacheKey, { ...currentCache, attachments: updated });
      showMessage("File deleted.", "success");
    } catch (err) {
      logger.error("Failed to delete attachment:", err);
      showMessage(err.message || "Could not delete file.", "error");
    }
  };

  return (
    <div className="house-rules-section">
      {isAnonymous && (
        <p className="house-rules-login-prompt">
          Log in to save house rules, notes, and files for this game.
        </p>
      )}
      <div className="house-rules-field">
        <label className="house-rules-label" htmlFor="house-rules-notes">
          Freeform notes
        </label>
        <textarea
          id="house-rules-notes"
          className="house-rules-textarea"
          value={notes}
          onChange={handleNotesChange}
          placeholder="Add your house rules, reminders, or other notes for this game..."
          disabled={!canEdit || isLoading}
          rows={8}
        />
        <button
          onClick={saveNotes}
          className="game-preview-action-btn primary house-rules-save"
          disabled={!canEdit || isSaving || isLoading}
          data-testid="house-rules-save-notes"
        >
          {isSaving ? "Saving..." : "Save notes"}
        </button>
      </div>

      <div className="house-rules-field">
        <label className="house-rules-label" htmlFor="house-rules-file">
          Documents and images
        </label>
        <p className="house-rules-note">
          You can upload multiple documents and images, but the total size for this game must stay under 5 MB.
        </p>
        <p className="house-rules-total-size">
          Currently using {totalMB.toFixed(2)} MB of 5 MB
        </p>
        <input
          id="house-rules-file"
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="house-rules-file-input"
          disabled={!canEdit || isUploading}
          accept="image/*,.pdf,.doc,.docx,.odt,.rtf,.txt"
          data-testid="house-rules-file-input"
        />
        {isUploading && <span className="house-rules-status">Uploading...</span>}
      </div>

      {attachments.length > 0 && (
        <ul className="house-rules-attachments">
          {attachments.map((a) => (
            <li key={a.id} className="house-rules-attachment">
              <button
                onClick={() => setSelectedAttachment(a)}
                className="house-rules-attachment-link"
                aria-label={`Open ${a.filename}`}
              >
                {a.filename}
              </button>
              {canEdit && (
                <button
                  onClick={() => deleteAttachment(a.id)}
                  className="house-rules-delete"
                  aria-label={`Delete ${a.filename}`}
                  data-testid={`house-rules-delete-${a.id}`}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {selectedAttachment && (
        <FileViewer
          attachment={selectedAttachment}
          onClose={() => setSelectedAttachment(null)}
        />
      )}

      {message && (
        <div className={`house-rules-message house-rules-message--${message.type}`}>
          {message.text}
        </div>
      )}
    </div>
  );
}

export default HouseRulesSection;
